(ns io.daskul.tutorial.webapp.components.attendance-roster
  "Reusable per-attendee attendance roster (SMS-148 Slice 3; absorbs SMS-140).

   A list of trial rows, each with quick Attended / No-show actions and a
   reversible undo. It is intentionally surface-agnostic so it serves BOTH the
   Lead-detail hub's Trial Bookings tab (SMS-148) and Today's trials
   (SMS-145) — the consumer supplies the rows (built by
   `views.lead-detail.vm/sa-rows` or its own builder) and an `:on-changed`
   callback to refetch its surrounding data after a mark/undo.

   Marking a SessionAttendee `attended` / `no-show` fires its FSM transition.
   The §4.4 cascade (server-side after-write on the SA tx-handler) auto-advances
   the linked Lead — so the consumer's `:on-changed` should refetch whatever
   state it shows (e.g. the lead, to flip its breadcrumb chip)."
  (:require
   [clojure.string :as str]
   [re-frame.core :as rf]
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.fsm.transitions :as fsm.transitions]))

;; ============================================================================
;; Mark / undo event
;; ============================================================================
;;
;; The generic ::agg.events/transition refetches the SA by id (+ its child
;; tabs) but knows nothing about the lead it cascades into, nor the lead-scoped
;; SA list this roster renders. So this wrapper fires the transition and, on
;; success, runs the consumer's :on-changed (which refetches the surface's
;; data) via the standard ::agg.events/transition machinery's re-fetch, plus an
;; explicit callback fired after a short settle so the cascade is visible.

(rf/reg-event-fx
 ::mark
 (fn [_ [_ {:keys [sa-id event on-changed]}]]
   {:dispatch [::agg.events/transition :session-attendee sa-id event nil]
    ;; Give the SA transition (+ its server-side §4.4 cascade) a beat to land,
    ;; then let the consumer refetch its surface (lead chip, list, …). The
    ;; transition itself already refetches the SA entity.
    :dispatch-later [{:ms 250 :dispatch [::after-changed on-changed]}]}))

(rf/reg-event-fx
 ::after-changed
 (fn [_ [_ on-changed]]
   (when on-changed (on-changed))
   {}))

;; ============================================================================
;; Row
;; ============================================================================

(defui attendance-row
  "One trial row. `row` is a flat view-model
   {:sa-id :state :chip {:label :color} :child-name :age :program-name :when
    :teacher :is-active? :made-up?}.
   `on-changed` (optional) is fired after a successful mark/undo."
  [{:keys [row on-changed]}]
  (let [{:keys [sa-id state chip child-name age program-name transitions is-active? made-up?]} row
        when-str (:when row)
        teacher (:teacher row)
        transitioning? (urf/use-subscribe [::agg.subs/transitioning? :session-attendee])
        transition-event (urf/use-subscribe [::agg.subs/transition-event :session-attendee])
        fire! (fn [event]
                (rf/dispatch [::mark {:sa-id sa-id :event event :on-changed on-changed}]))
        scheduled? (= "scheduled" state)
        attended? (= "attended" state)
        no-show? (= "no-show" state)
        ;; spinner only while THIS roster's SA is mid-transition
        busy? transitioning?
        meta-parts (->> [(when (some? age) (str "Age " age))
                         program-name
                         when-str
                         (when teacher (str "with " teacher))]
                        (filter some?))]
    ($ ui/paper {:withBorder true :p "sm" :radius "md"
                 :data-testid "attendance-row"
                 :data-sa-id (str sa-id)
                 :data-sa-state state}
       ($ ui/group {:justify "space-between" :wrap "nowrap" :align "flex-start"}
          ;; Identity + meta
          ($ ui/stack {:gap 2 :style {:minWidth 0 :flex 1}}
             ($ ui/group {:gap "xs" :wrap "nowrap"}
                ($ ui/text {:fw 600 :size "sm"
                            :style {:fontFamily "Sora, sans-serif"}}
                   child-name)
                ($ ui/badge {:color (:color chip) :variant "light" :size "sm"
                             :data-testid "attendance-status"}
                   (:label chip))
                ;; SMS-171: a no-show/cancelled row superseded by a later
                ;; makeup keeps its original state (that's the point — the
                ;; old booking stays as history), so its status chip alone
                ;; doesn't show it's been handled. This tag fills that gap.
                (when made-up?
                  ($ ui/badge {:color "grape" :variant "light" :size "sm"
                               :data-testid "made-up-tag"}
                     "Made Up")))
             (when (seq meta-parts)
               ($ ui/text {:size "xs" :c "dimmed"}
                  (str/join " · " meta-parts))))

          ;; Actions
          ($ ui/group {:gap "xs" :wrap "nowrap"}
             (cond
               scheduled?
               ;; Forward quick actions are FSM-driven via `transition-action`
               ;; (SMS-162): it derives a param/confirm modal from each
               ;; transition's def (:_meta.fsm) — Attended fires directly (no
               ;; params); No-show opens the Waive-Credit?/Reason modal instead
               ;; of the old param-less button that 422'd. Label/icon/variant
               ;; live in the FSM :ui defs (single source of truth).
               (let [tx-of (fn [event] (some #(when (= event (:event %)) %) transitions))]
                 ($ :<>
                    (when-let [attended-tx (tx-of "mark-attended")]
                      ($ fsm.transitions/transition-action
                         {:transition attended-tx
                          :aggregate  :session-attendee
                          :record-id  sa-id
                          :on-success on-changed
                          :size       "xs"
                          :data-testid "mark-attended"}))
                    (when-let [no-show-tx (tx-of "mark-no-show")]
                      ($ fsm.transitions/transition-action
                         {:transition no-show-tx
                          :aggregate  :session-attendee
                          :record-id  sa-id
                          :on-success on-changed
                          :size       "xs"
                          :data-testid "mark-no-show"}))))

               ;; Undo only renders on the lead's currently-active SA
               ;; (:is-active?, set by vm/sa-rows — the most recently created
               ;; non-:rescheduled SA). A superseded :attended/:no-show row
               ;; (e.g. one a makeup or reschedule already replaced) shows no
               ;; action — undoing it would resurrect a booking that's no
               ;; longer current (SMS-165/171; backend rejects it too).
               (and attended? is-active?)
               ($ ui/tooltip {:label "Undo — back to scheduled" :withArrow true}
                  ($ ui/action-icon
                     {:variant "subtle" :color "gray" :radius "xl"
                      :loading (and busy? (= "unmark-attended" transition-event))
                      :data-testid "undo-attended"
                      :onClick #(fire! "unmark-attended")}
                     ($ icons/icon-refresh {:size 16})))

               (and no-show? is-active?)
               ($ ui/tooltip {:label "Undo — back to scheduled" :withArrow true}
                  ($ ui/action-icon
                     {:variant "subtle" :color "gray" :radius "xl"
                      :loading (and busy? (= "unmark-no-show" transition-event))
                      :data-testid "undo-no-show"
                      :onClick #(fire! "unmark-no-show")}
                     ($ icons/icon-refresh {:size 16})))

               :else nil))))))

(defui attendance-roster
  "Renders a list of attendance rows. Props:
     :rows       — vector of row view-models (see attendance-row)
     :on-changed — (optional) (fn []) fired after a successful mark/undo so the
                   consumer refetches its surface (e.g. the lead chip + list)
     :empty      — (optional) element/string shown when rows is empty"
  [{:keys [rows on-changed empty]}]
  (if (seq rows)
    ($ ui/stack {:gap "sm"}
       (for [row rows]
         ($ attendance-row {:key (str (:sa-id row)) :row row :on-changed on-changed})))
    ($ ui/text {:c "dimmed" :size "sm"} (or empty "No trial bookings yet."))))
